import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { useFeatures } from '@lifeomic/phc-web-toolkit/src/hooks';
import DeleteConsentButton from './DeleteConsentButton';
import { ReassignMemberConsentRowButton } from './ReassignMemberConsentRowButton';
import { AssignableConsentMenu } from './AssignableConsentMenu';

const useStyles = makeStyles(() => ({
  cell: {
    whiteSpace: 'break-spaces',
  },
}));

const messages = defineMessages({
  name: {
    id: 'patientViewer.consentList.headers.name',
    defaultMessage: 'Name',
  },
  status: {
    id: 'patientViewer.consentList.headers.status',
    defaultMessage: 'Status',
  },
  version: {
    id: 'patientViewer.consentList.headers.version',
    defaultMessage: 'Version',
  },
  date: {
    id: 'patientViewer.consentList.headers.date',
    defaultMessage: 'Date',
  },
  acceptedHeader: {
    id: 'patientViewer.consentList.headers.acceptedHeader',
    defaultMessage: 'Accepted?',
  },
  acceptedValue: {
    id: 'patientViewer.consentList.headers.acceptedHeader',
    defaultMessage: 'Accepted',
  },
});

const canRemoveConsent = consent => {
  const status = consent?.status;
  return status === 'proposed' || status === 'active';
};

const ConsentList = ({
  patient,
  listContextKey,
  data,
  onConsentSelect,
  isFetching,
  onConsentDelete,
  onConsentAssigned,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const hasFeature = useFeatures();
  const showDelete = hasFeature('deleteConsent') && onConsentDelete;
  const showRowActions = hasFeature('memberOpsPhc') || showDelete;

  const sorted = [...data];
  sorted.sort(
    (consent1 = {}, consent2 = {}) => consent2.sortValue - consent1.sortValue,
  );

  const columns = [
    {
      header: {
        label: formatMessage(messages.name),
      },
      cell: {
        className: classes.cell,
        content: consent => consent?.name,
      },
    },
    {
      header: {
        label: formatMessage(messages.status),
      },
      cell: {
        content: consent => consent?.status,
      },
    },
    {
      header: {
        label: formatMessage(messages.acceptedHeader),
      },
      cell: {
        content: consent => {
          const isAccepted = consent?.originalStatus === 'active';
          return isAccepted ? formatMessage(messages.acceptedValue) : '';
        },
      },
    },
    {
      header: {
        label: formatMessage(messages.version),
      },
      cell: {
        content: consent => consent?.version,
      },
    },
    {
      header: {
        label: formatMessage(messages.date),
      },
      cell: {
        className: classes.cell,
        content: consent => consent?.date,
      },
    },
  ];

  return (
    <>
      {hasFeature('memberOpsPhc') && (
        <AssignableConsentMenu
          patientId={patient.id}
          onConsentAssigned={onConsentAssigned}
        />
      )}
      <TableModule
        config={columns}
        data={sorted}
        isLoading={isFetching}
        onRowClick={onConsentSelect}
        wrapCellContent
        rowActions={
          showRowActions
            ? consent => (
                <>
                  {hasFeature('memberOpsPhc') && (
                    <ReassignMemberConsentRowButton
                      patient={patient}
                      listContextKey={listContextKey}
                      consent={consent}
                    />
                  )}
                  {showDelete && canRemoveConsent(consent) ? (
                    <DeleteConsentButton
                      directiveId={consent.id}
                      consentName={consent.name || ''}
                      consentDate={consent.date || ''}
                      isAccepted={
                        consent.originalStatus === 'active' ||
                        consent.status === 'active'
                      }
                      onConsentDelete={onConsentDelete}
                    />
                  ) : null}
                </>
              )
            : undefined
        }
      />
    </>
  );
};

ConsentList.propTypes = {
  patient: PropTypes.object.isRequired,
  listContextKey: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.object),
  onConsentSelect: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  onConsentDelete: PropTypes.func,
  onConsentAssigned: PropTypes.func.isRequired,
};

export default ConsentList;
